import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, TextBox, Button, Card, Icon, ModalWrapper, ModalCloseButton } from '../../../components/common';
import { useAuth } from '../../../contexts/AuthContext';
import config from '../../../config';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, login, socialLogin } = useAuth();
  const redirectTo = location.state?.from?.pathname || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState({});

  // #8: 이미 로그인된 상태면 홈으로 리다이렉트
  React.useEffect(() => {
    if (!loading && user) navigate(redirectTo, { replace: true });
  }, [loading, user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!email.trim()) newErrors.email = t('auth.alert_email_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = t('auth.alert_email_invalid');
    if (!password.trim()) newErrors.password = t('auth.alert_password_required');
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(redirectTo);
    } catch (err) {
      alert(t('auth.alert_login_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleKakaoLogin = async () => {
    try {
      sessionStorage.setItem('loginRedirect', redirectTo);
      await loadScript('https://t1.kakaocdn.net/kakao_js_sdk/2.7.6/kakao.min.js');
      if (!window.Kakao.isInitialized()) window.Kakao.init(config.KAKAO_APP_KEY);
      window.Kakao.Auth.authorize({
        redirectUri: `${window.location.origin}/auth/kakao/callback`,
        // scope 는 코드에서 명시하지 않고 카카오 콘솔의 "동의항목 사용 ON" 상태를 그대로 따른다.
        // 코드에 명시한 항목이 콘솔에 OFF 면 KOE205 발생 (account_email/phone_number 는 비즈 앱 검수 필요).
        // 콘솔 ON 된 항목 (예: profile_nickname) 만 자동으로 동의창에 표시 → 검수/신청 진행에 따라 점진 추가됨.
      });
    } catch (err) {
      alert(t('auth.alert_kakao_login_failed'));
    }
  };

  const handleNaverLogin = async () => {
    try {
      sessionStorage.setItem('loginRedirect', redirectTo);
      const callbackUrl = `${window.location.origin}/auth/naver/callback`;
      const state = Math.random().toString(36).substring(2);
      window.location.href = `https://nid.naver.com/oauth2.0/authorize?response_type=token&client_id=${config.NAVER_CLIENT_ID}&redirect_uri=${encodeURIComponent(callbackUrl)}&state=${state}`;
    } catch (err) {
      alert(t('auth.alert_naver_login_failed'));
    }
  };

  const [showInAppModal, setShowInAppModal] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const handleGoogleLogin = () => {
    try {
      // 인앱 브라우저(WebView) 감지
      const ua = navigator.userAgent || '';
      const isInApp = /NAVER|KAKAOTALK|Line|Instagram|FBAN|FBAV|Twitter|wv/i.test(ua);
      if (isInApp) {
        const siteUrl = window.location.origin + '/login';
        navigator.clipboard.writeText(siteUrl).then(() => setUrlCopied(true)).catch(() => setUrlCopied(false));
        setShowInAppModal(true);
        return;
      }

      sessionStorage.setItem('loginRedirect', redirectTo);
      const callbackUrl = `${window.location.origin}/auth/google/callback`;
      const params = new URLSearchParams({
        client_id: config.GOOGLE_CLIENT_ID,
        redirect_uri: callbackUrl,
        response_type: 'code',
        // 구글 People API 의 phoneNumbers.read 스코프 포함 — BE 가 People API 호출해서 phone 추출
        scope: 'openid email profile https://www.googleapis.com/auth/user.phonenumbers.read',
        access_type: 'offline',
        prompt: 'select_account',
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    } catch (err) {
      alert(t('auth.alert_google_login_failed'));
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-[calc(100vh-59px)] bg-mono-50">
        <div className="w-full max-w-narrow mx-auto px-2.5 sm:px-5 pt-8 sm:pt-10 pb-12 sm:pb-16">
          {/* Logo */}
          <div className="flex justify-center"><img src="/images/logo/logo2.png" alt="VIP VAN TOUR" className="h-10" /></div>

          {/* Card */}
          <Card variant="auth" className="mt-4">
            <h2 className="text-xl font-semibold text-mono-900 text-center mb-6">{t('common.login')}</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <TextBox
                label={t('auth.email')}
                placeholder={t('auth.email_placeholder')}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: '' })); }}
                error={errors.email}
              />
              <TextBox
                label={t('auth.password')}
                placeholder={t('auth.password_placeholder')}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: '' })); }}
                type="password"
                error={errors.password}
              />

              <Button type="submit" level="level1" fullWidth disabled={submitting}>
                {submitting ? t('common.loading') : t('common.login')}
              </Button>

              <Button
                type="button"
                level="level3"
                fullWidth
                onClick={() => navigate('/signup')}
              >
                {t('common.signup')}
              </Button>
            </form>

            {/* Divider */}
            <div className="my-4 border-t border-mono-100" />

            {/* Social Login Buttons */}
            <div className="space-y-3">
              {/* Kakao */}
              <button
                onClick={handleKakaoLogin}
                className="w-full h-12 rounded-lg bg-kakao-bg text-kakao-text font-bold text-2sm flex items-center justify-center hover:opacity-90 transition"
              >
                <span className="inline-flex items-center gap-2">
                  <img src="/images/kakao-logo.svg" alt="" className="w-5 h-5 shrink-0" />
                  {t('auth.kakao_login')}
                </span>
              </button>

              {/* Naver */}
              <button
                onClick={handleNaverLogin}
                className="w-full h-12 rounded-lg bg-naver-bg text-white font-bold text-2sm flex items-center justify-center hover:opacity-90 transition"
              >
                <span className="inline-flex items-center gap-2">
                  <img src="/images/naver-logo.svg" alt="" className="w-5 h-5 shrink-0" />
                  {t('auth.naver_login')}
                </span>
              </button>

              {/* Google */}
              <button
                onClick={handleGoogleLogin}
                className="w-full h-12 rounded-lg bg-white border border-mono-200 text-mono-900 font-bold text-2sm flex items-center justify-center hover:bg-mono-50 transition"
              >
                <span className="inline-flex items-center gap-2">
                  <img src="/images/google-logo.svg" alt="" className="w-5 h-5 shrink-0" />
                  {t('auth.google_login')}
                </span>
              </button>
            </div>

            {/* Bottom Links */}
            <div className="mt-4 space-y-2">
              <Link
                to="/reservation/guest"
                className="block text-sm font-bold text-mono-900 text-center hover:text-orange-500 transition"
              >
                {t('nav.reservation_lookup')}
              </Link>
              <Link
                to="/reset-password"
                className="block text-sm text-mono-400 text-center hover:text-mono-500 transition"
              >
                {t('auth.forgot_password')}
              </Link>
            </div>
          </Card>
        </div>
      </main>

      {/* 인앱 브라우저 구글 로그인 안내 모달 */}
      {showInAppModal && (
        <ModalWrapper padding="px-5 pt-5 pb-8" gap="gap-2.5" onClose={() => setShowInAppModal(false)}>
          <div className="flex justify-end">
            <ModalCloseButton onClick={() => setShowInAppModal(false)} />
          </div>

          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center">
              <Icon name="notification" size={52} className="text-orange-500" />
            </div>
          </div>

          <h2 className="text-xl font-bold text-mono-900 text-center mt-2">
            {t('auth.inapp_browser_title')}
          </h2>

          <p className="text-sm font-medium text-mono-900 text-center leading-relaxed break-keep whitespace-pre-line">
            {t('auth.inapp_browser_desc')}
          </p>

          {urlCopied && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-center gap-2">
              <Icon name="check" size={16} className="text-green-600" />
              <span className="text-sm font-bold text-green-700">{t('auth.inapp_url_copied')}</span>
            </div>
          )}

          <p className="text-xs text-mono-400 text-center leading-relaxed">
            {t('auth.inapp_instruction')}
          </p>

          <div className="flex flex-col gap-2.5 mt-2">
            <Button
              level="level1"
              fullWidth
              onClick={() => {
                const siteUrl = window.location.origin + '/login';
                navigator.clipboard.writeText(siteUrl).then(() => setUrlCopied(true)).catch(() => {});
              }}
            >
              {urlCopied ? t('auth.inapp_copy_done') : t('auth.inapp_copy_again')}
            </Button>
            <Button level="level5" fullWidth onClick={() => setShowInAppModal(false)}>
              {t('common.close')}
            </Button>
          </div>
        </ModalWrapper>
      )}
    </>
  );
}
